import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetAuthor,
  useUpdateAuthor,
} from "@/api/author/author";
import { authorSchema } from "@/api/author/author-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { FormLayout } from "@/layout/FormLayout";
import { Author } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Separator } from "@radix-ui/react-separator";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const EditAuthor = ({ author }: { author: Author }) => {
  const form = useForm({
    resolver: standardSchemaResolver(authorSchema.passthrough()),
    defaultValues: author,
    reValidateMode: "onSubmit",
  });

  const { data: attachments } = useGetAttachmentsByDocId(author.id);

  const authorUpdater = useUpdateAuthor();

  const { t } = useTranslation();

  const { relativePosition } = useDashboardParams<Author>({
    statusField: "artistesAuteursPublicationStatus",
    resourceName: "author",
    relativePosition: {
      currentId: author.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  const [, setSearchParam] = useSearchParams();
  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            authorUpdater.mutate(
              {
                ...values,
                ...(status && { artistesAuteursPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("authorSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.artistesAuteursDescriptionNom != undefined)
              setSearchParam({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({ queryKey: ["authors"] });
        queryClient.invalidateQueries({ queryKey: ["author", author.id] });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: author.id }],
        });
      }}
      relativePosition={relativePosition}
      title={`${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`}
      publicationStatus={author.artistesAuteursPublicationStatus}
      subtitle={
        author.artistesAuteursDescriptionResumeDates &&
        `(${author.artistesAuteursDescriptionResumeDates})`
      }
      id={author.id}
      type="author"
      picturesData={attachments && { pictures: attachments, type: "author" }}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={
          [
            {
              value: "description",
              label: t("authorDescription"),
            },
            {
              value: "sources",
              label: t("sources"),
            },
            { value: "file", label: t("fichiers") },
            { value: "links", label: t("linkedEntries") },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Text name="artistesAuteursDescriptionPrenom" />
            <AF.Text name="artistesAuteursDescriptionNom" />
            <AF.Text name="artistesAuteursDescriptionEtatCivil" />

            <AF.Row>
              <AF.Text name="artistesAuteursDescriptionDateNaissance" />
              <AF.Text name="artistesAuteursDescriptionVilleDeNaissance" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="artistesAuteursDescriptionDateDeDeces" />
              <AF.Text name="artistesAuteursDescriptionVilleDeDeces" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="artistesAuteursDescriptionResumeDates" />
              <AF.Text name="artistesAuteursDescriptionNationalite" />
            </AF.Row>
            <ModifiableList
              name="artistesAuteursDescriptionPaysDActivite"
              listKey="liste-pays"
            />
            <ModifiableList
              name="artistesAuteursDescriptionActivite"
              listKey="liste-activites"
            />
            <ModifiableList
              name="artistesAuteursDescriptionPeriodeDactivit"
              listKey="liste-periodes-creation"
            />
            <AF.RichText name="artistesAuteursDescriptionNotesBiographiques" />
          </AF.Col>
          <AF.Col>
            <AF.RichText name="artistesAuteursDescriptionNotesDeTravail" />
            <AF.Text name="artistesAuteursAyantDroitsNom" />
            <AF.Text name="artistesAuteursAyantDroitsContact" />

            <AF.Text name="artistesAuteursAyantDroitsLienAvecLAuteur" />
            <AF.Text name="artistesAuteursAyantDroitsOrganismeGerantLesDroits" />
            <AF.Text name="artistesAuteursExpertCrNom" />
            <AF.Text name="artistesAuteursExpertCrContact" />
            <AF.Checkbox name="artistesAuteursExpertCrCatalogueRaisonne" />
            <AF.Text name="artistesAuteursExpertCrEtatDuCatalogueRaisonne" />
            <AF.RichText name="artistesAuteursExpertCrDescriptionDuCatalogueRaisonne" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="file"
          headerExtras={<AddFileModal id={author.id} />}
        >
          <AF.OthersFilesTable docId={author.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={author.id}
            type="author"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={author}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditAuthorPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetAuthor(id);

  return (
    <>
      {data && <EditAuthor key={data.id} author={data} />}
      {!data && !isLoading && <h2 className="p-4">auteurNExistePasLabel</h2>}
    </>
  );
};
